import React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import Image from 'next/image'
import cx from 'classnames'
import Script from 'next/script'

import { GAME_TYPE } from 'lib/constants'

import Button from 'components/Button'

import s from './landing.module.css'

const signatureImg = '/images/signature.png'
const reyImg = '/gamesAssets/gridattack/rey.png'
const lukeImg = '/gamesAssets/gridattack/luke.png'
const valcorianImg = '/gamesAssets/gridattack/m-valcorian.png'

const StartGameButton = ({ game }: { game: GAME_TYPE }) => {
  const router = useRouter()

  const { color } = game
  return (
    <Link href={`/games/${router?.query?.gameUrl}/play`} passHref>
      <Button tag="a" btnColor={`bg-${color}-600`} btnHoverColor={`bg-${color}-700`}>
        Start Quest & Learn Grid →
      </Button>
    </Link>
  )
}

const Landing = ({ game }: { game: GAME_TYPE }) => {
  return (
    <>
      <div className={cx('bg-black w-full h-full text-white py-16 min-h-screen px-4 relative')}>
        <div className={s.heroBackground}></div>
        <div className={cx('flex flex-col items-center z-10 relative')}>
          <div className="relative h-48 sm:h-64 w-full mb-6">
            <Image
              src={'/gamesAssets/gridattack/landing-top.png'}
              layout="fill"
              quality={100}
              objectFit="contain"
              alt="Rey, Valcorian and Luke from CSS Grid Attack Game"
            />
          </div>
          <div className="w-full">
            <h1
              className={cx(
                s.highlightTitle,
                'mb-12 lg:text-7xl text-5xl font-bold text-center text-red-500'
              )}
            >
              Grid Attack
            </h1>
            <div className="lg:max-w-xl max-w-md m-auto text-gray-200">
              <p className="mb-6 lg:text-2xl md:text-xl text-lg">
                In an ancient Elvish prophecy, it was said that one day a man would be born with an
                incredible power that predicts the future – "Marketi Predictori." And another will
                come to take this power. But the years went by and nothing happened.{' '}
                <span className="font-medium">
                  Until one day, a little elf was born. He was named Luke.
                </span>
              </p>
              <p className="mb-6 lg:text-2xl md:text-xl text-lg w-full">
                From an early age, he surprised his parents and his sister Rey by guessing the price
                of apples at the farmer's market before they even reached it. Every year his power
                rose and his predictions became more and more accurate.
                <span className="font-medium">
                  {' '}
                  But there was one thing Luke could not predict. The coming of the demon Valcorian.
                  It was the one from the prophecy that was to come and take Luke's power.
                </span>{' '}
                One day Valcorian and his army attacked the town where Luke had lived and kidnapped
                him to make a ritual of stealing his power.
              </p>
              <p className="mb-6 lg:text-2xl md:text-xl text-lg w-full">
                Go on a dangerous quest with Luke's sister Rey and find her brother.{' '}
                <span className="font-medium">
                  Defeat Valcorian and all his demons using a secret weapon – CSS Grid.
                </span>
              </p>
              {/* <p className="lg:text-2xl md:text-xl text-lg w-full">
                Are you ready to become confident with CSS Grid, start making web pages much faster
                and never waste time looking into cheat codes and docs?
              </p> */}
            </div>
            <div className="w-full flex justify-center mt-12">
              <StartGameButton game={game} />
            </div>
          </div>
        </div>
      </div>
      <div className={cx('bg-black w-full h-full lg:py-16 py-8 px-4 relative')}>
        <h2
          className={cx(
            s.highlightText,
            'lg:text-7xl md:text-5xl text-4xl text-white font-bold text-center mb-12'
          )}
        >
          We made this game for <span className={'text-red-500'}>You?</span>
        </h2>
        <div className="lg:max-w-xl max-w-md m-auto  lg:mb-36 mb-16 ">
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-6">
            1. You often stumble and try to figure out which combination of Grid properties makes
            the browser do what you want it to do.
          </p>
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-6 font-medium">
            2. You are scared by the number of properties a CSS Grid has, and you feel uncomfortable
            when you need to create a grid layout.
          </p>
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-6">
            3. You want to create complex web layouts using Grid, but without constantly looking at
            the web page after every "Cmd/Ctrl+S" press in the code editor.
          </p>
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-6 font-medium">
            4. You have tried to learn CSS Grid with video tutorials and articles but still don't
            fully understand how some parts of it work.
          </p>
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-6">
            5. You use a Flex Box where Grid is required because you don't feel confident in using
            it.
          </p>
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg">
            Is this you? Then you definitely want to learn Grid or improve upon your current skills.
            We have good news for you… really good news!
          </p>
        </div>
        <h2
          className={cx(
            s.highlightText,
            'lg:text-7xl md:text-5xl text-4xl text-white font-bold text-center mb-12'
          )}
        >
          Learn Grid by <span className={'text-red-500'}>Playing CSS Game</span>
        </h2>
        <div className="lg:max-w-xl max-w-md m-auto mb-6">
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-6">
            No more boring videos, courses and articles. Learn Grid in a revolutionary new, fun, and
            effective way. By playing a Grid coding game!
          </p>
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg">What's included:</p>
        </div>
        <div className="lg:max-w-xl max-w-md m-auto mb-2">
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg">
            <span className="font-bold text-purple-500">+ 80 coding levels</span> that will teach
            you in detail how to use each CSS Grid property with all its nuances.
          </p>
        </div>
        <div className="lg:max-w-xl max-w-md m-auto mb-2">
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg">
            <span className="font-bold text-pink-500">+ 40 real-project layouts</span> that you will
            learn how to create by building them yourself.
          </p>
        </div>
        <div className="lg:max-w-xl max-w-md m-auto mb-2">
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg">
            <span className="font-bold text-yellow-500">+ 3 game modes</span>: Easy, Medium, and
            Hard for all levels of CSS Grid knowledge.
          </p>
        </div>
        <div className="lg:max-w-xl max-w-md m-auto mb-12">
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg">
            <span className="font-bold text-green-500">
              + Interactive and fun-to-play environment
            </span>{' '}
            that makes learning feel like play.
          </p>
        </div>
        <div className={cx(s.funToPlayImage, 'relative w-full lg:mb-36 mb-16')}>
          <Image
            src={'/gamesAssets/gridattack/fun-to-play.png'}
            layout="fill"
            quality={100}
            objectFit="contain"
            alt="Rey versus Valcorian at CSS Grid Attack Game"
          />
        </div>
        <h2
          className={cx(
            s.highlightText,
            'lg:text-7xl md:text-5xl text-4xl text-white font-bold text-center mb-6'
          )}
        >
          What you will learn
        </h2>
        <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-12 text-center">
          You will completely learn CSS Grid in every detail once and for all.
        </p>
        <div className="max-w-5x xl:max-w-screen-2xl m-auto xl:mb-36 mb-16 grid xl:grid-cols-2 grid-cols-1 gap-x-8 xl:gap-y-16 gap-y-8">
          <div className="w-full">
            <video
              className="relative w-full mb-6 m-auto border-2 border-gray-400"
              autoPlay
              loop
              muted
              playsInline
              style={{ aspectRatio: '1599  /  900' }}
            >
              <source src={'/gamesAssets/gridattack/1.mp4'} />
            </video>
            <div className="text-left">
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-2">
                + What are <span className="text-red-500">grid-template-columns</span> and{' '}
                <span className="text-red-500">grid-template-rows</span> and how to use them with
                real-world layouts
              </p>
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-2">
                + How to effectively use the <span className="text-red-500">repeat</span> function
                and stop wasting priceless time repeating yourself
              </p>
            </div>
          </div>
          <div className="w-full">
            <video
              className="relative w-full mb-6 m-auto border-2 border-gray-400"
              autoPlay
              loop
              muted
              playsInline
              style={{ aspectRatio: '1599  /  900' }}
            >
              <source src={'/gamesAssets/gridattack/4.mp4'} />
            </video>
            <div className="text-left">
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-2">
                + How to make a perfect gap in a grid using{' '}
                <span className="text-green-500">column-gap</span> and{' '}
                <span className="text-green-500">row-gap</span>
              </p>
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-2">
                + Learn a shortened property to make a gap using{' '}
                <span className="text-green-500">gap</span>
              </p>
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg">
                + What is the new magical <span className="text-green-500">fr</span> unit, how is it
                different from <span className="text-green-500">%</span> and which one to use where
              </p>
            </div>
          </div>
          <div className="w-full">
            <video
              className="relative w-full mb-6 m-auto border-2 border-gray-400"
              autoPlay
              loop
              muted
              playsInline
              style={{ aspectRatio: '1599  /  900' }}
            >
              <source src={'/gamesAssets/gridattack/5.mp4'} />
            </video>
            <div className="text-left">
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-2">
                + How to size and position items using{' '}
                <span className="text-indigo-500">grid-row-start/end</span> and{' '}
                <span className="text-indigo-500">grid-column-start/end</span> to create an advanced
                grid
              </p>
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-2">
                + How to perfectly position items using{' '}
                <span className="text-indigo-500">grid-row</span> and{' '}
                <span className="text-indigo-500">grid-column</span>
              </p>
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg">
                + What are mystical <span className="text-indigo-500">grid-template-areas</span> and{' '}
                <span className="text-indigo-500">grid-areas</span> properties and how to use them
                with modern layouts
              </p>
            </div>
          </div>
          <div className="w-full">
            <video
              className="relative w-full mb-6 m-auto border-2 border-gray-400"
              autoPlay
              loop
              muted
              playsInline
              style={{ aspectRatio: '1599  /  900' }}
            >
              <source src={'/gamesAssets/gridattack/6.mp4'} />
            </video>
            <div className="text-left">
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-2">
                + Lean the most effective grid function{' '}
                <span className="text-blue-500">minmax</span> and how to create flexible tracks
                using it
              </p>
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-2">
                + How to use <span className="text-blue-500">min-content</span> and{' '}
                <span className="text-blue-500">max-content</span> to create a responsive grid
              </p>
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg">
                + How and where to use <span className="text-blue-500">auto-fill</span> and{' '}
                <span className="text-blue-500">auto-fit</span> to make your grid flexible
              </p>
            </div>
          </div>
          <div className="w-full">
            <video
              className="relative w-full mb-6 m-auto border-2 border-gray-400"
              autoPlay
              loop
              muted
              playsInline
              style={{ aspectRatio: '1599  /  900' }}
            >
              <source src={'/gamesAssets/gridattack/8.mp4'} />
            </video>
            <div className="text-left">
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-2">
                + How items <span className="text-yellow-500">flow</span> in a grid and why it's
                extremely important to know
              </p>
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-2">
                + How to use <span className="text-yellow-500">grid-auto-flow</span> to magically
                change items flow
              </p>
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg">
                + The difference between <span className="text-yellow-500">column</span>,{' '}
                <span className="text-yellow-500">dense</span>, and{' '}
                <span className="text-yellow-500">row</span> flow
              </p>
            </div>
          </div>
          <div className="w-full">
            <video
              className="relative w-full mb-6 m-auto border-2 border-gray-400"
              autoPlay
              loop
              muted
              playsInline
              style={{ aspectRatio: '1599  /  900' }}
            >
              <source src={'/gamesAssets/gridattack/9.mp4'} />
            </video>
            <div className="text-left">
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-2">
                + Learn about hidden <span className="text-purple-500">implicit</span> grid and how
                its difference from known <span className="text-purple-500">explicit</span> grid
              </p>
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-2">
                + How to use <span className="text-purple-500">grid-auto-columns</span> to create a
                hidden grid
              </p>
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg">
                + How to use <span className="text-purple-500">grid-auto-rows</span> to change the
                way you will work with grids forever
              </p>
            </div>
          </div>
          <div className="w-full">
            <video
              className="relative w-full mb-6 m-auto border-2 border-gray-400"
              autoPlay
              loop
              muted
              playsInline
              style={{ aspectRatio: '1599  /  900' }}
            >
              <source src={'/gamesAssets/gridattack/10.mp4'} />
            </video>
            <div className="text-left">
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-2">
                + Learn how to position items with{' '}
                <span className="text-pink-500">justify-items</span>,{' '}
                <span className="text-pink-500">align-items</span>, and{' '}
                <span className="text-pink-500">place-items</span> and forget about flex box
              </p>
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-2">
                + How to effectively use <span className="text-pink-500">justify-self</span>,{' '}
                <span className="text-pink-500">align-self</span>, and{' '}
                <span className="text-pink-500">place-self</span>
              </p>
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg">
                + How to replace flex box with when it is unnecessary with{' '}
                <span className="text-pink-500">justify-content</span> and{' '}
                <span className="text-pink-500">align-content</span>
              </p>
            </div>
          </div>
          <div className="w-full">
            <video
              className="relative w-full mb-6 m-auto border-2 border-gray-400"
              autoPlay
              loop
              muted
              playsInline
              style={{ aspectRatio: '1599  /  900' }}
            >
              <source src={'/gamesAssets/gridattack/11.mp4'} />
            </video>
            <div className="text-left">
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-2">
                <span className="text-purple-500">+ Master CSS Grid</span> by solving complex
                interactive levels
              </p>
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-2">
                <span className="text-yellow-500">+ See how the story ends</span> by defeating
                challenging bosses
              </p>
              <p className="text-gray-200 lg:text-2xl md:text-xl text-lg">
                <span className="text-green-500">+ Learn 8 bonus facts</span> about CSS Grid
              </p>
            </div>
          </div>
        </div>
        <h2
          className={cx(
            s.highlightText,
            'lg:text-7xl md:text-5xl text-4xl text-white font-bold text-center mb-6'
          )}
        >
          Ready for your <span className={'text-red-500'}>Dangerous Quest?</span>
        </h2>
        <h2
          className={cx(
            s.highlightText,
            'text-gray-200 lg:text-2xl md:text-xl text-lg lg:max-w-3xl max-w-md  w-full m-auto text-center'
          )}
        >
          Become super confident with CSS Grid and start making web pages without scouring the
          internet for answers.
        </h2>
        <div className="lg:max-w-xl max-w-md m-auto lg:mb-36 mb-16">
          <div className="w-full flex justify-center lg:mt-12 mt-6">
            <StartGameButton game={game} />
          </div>
        </div>
        {/* <h2
          className={cx(
            s.highlightText,
            'lg:text-7xl md:text-5xl text-4xl text-white font-bold text-center mb-6'
          )}
        >
          Our <span className={cx('text-blue-500')}>Mission</span>
        </h2> */}
        {/* <p className="text-gray-200 lg:text-4xl md:text-3xl text-2xl lg:mb-12 mb-6 text-center">
          Making learning to code more <span className="text-flexbox-500">efficient</span>,{' '}
          <span className="text-green-500">interesting</span> and{' '}
          <span className="text-yellow-500">fun</span>.
        </p>
        <div className="lg:max-w-xl max-w-md m-auto lg:mb-36 mb-16">
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-6">
            Hi, my name is{' '}
            <a
              href="https://twitter.com/nickbulljs?ref=codingfantasy.com"
              target="_blank"
              className="border-b-2 border-dotted border-gray-200"
            >
              Nick
            </a>
            . I'm the founder of Coding Fantasy. I've been programming for over 7 years and I've
            learned to code using traditional ways.
          </p>
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-6">
            I remember those days. How often I forced myself to sit and learn to code. How boring
            articles and documentation seemed to me. How much I lacked practice and because of this
            knowledge quickly disappeared.{' '}
            <span className="font-medium">
              Learning shouldn't be like that, so I decided to change it.
            </span>
          </p>
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-6">
            I started with the fundamentals and studied how our brain learns. Neuroscience{' '}
            <a
              href="https://trainingindustry.com/articles/content-development/how-the-brain-learns/"
              target="_blank"
              rel="noreferrer"
              className="border-b-2 border-dotted text-gray-200 "
            >
              says
            </a>{' '}
            that{' '}
            <span className="font-medium">
              effective learning requires (1) a lot of practice and (2) a sense that learning is
              engaging
            </span>
            . Exactly what is often missed in traditional ways of learning. Exactly why I had a hard
            time learning to code. And exactly what needed to be changed.
          </p>
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-6">
            I've created Coding Fantasy as a new and more efficient way to learn code. Games are one
            of the best combination of (1) practice and (2) an engaging learning environment. They
            are the next logical step of traditional education. They are the future of learning. And{' '}
            <span className="font-medium">
              my mission and mission of the Coding Fantasy is to make that future our reality.
            </span>
          </p>
          <p className="text-gray-200 text-lg mb-6 relative italic flex flex-col items-end">
            <div className="h-20 w-40 relative">
              <Image
                src={signatureImg}
                layout="fill"
                quality={100}
                objectFit="contain"
                alt="Flexbox Terminology"
              />
            </div>
            <p>Nick Bull, Founder of Coding Fantasy</p>
          </p>
        </div> */}
        <h2
          className={cx(
            s.highlightText,
            'lg:text-7xl md:text-5xl text-4xl text-white font-bold text-center mb-12'
          )}
        >
          <span className="text-yellow-500">FAQ 🤝</span>
        </h2>
        <div className="lg:max-w-xl max-w-md mx-auto">
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg font-medium mb-2">
            Question: Does the skills learned in the game transfers to real world coding?
          </p>
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-6">
            Answer: Yes. All skills you'll during the game are the skills you'll need to solve in
            real world problems and you'll be able to use them in your real world projects.
          </p>
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg font-medium mb-2">
            Question: Does the problems I'll solve in the game are real world problems?
          </p>
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-6">
            Answer: Yes. Problems that you'll learn how to solve in the game are the problems you
            will encounter in real world projects.
          </p>
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg font-medium mb-2">
            Question: Does the layouts I'll create in the game are real world layouts?
          </p>
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-6">
            Answer: Yes. During the game you'll learn how to create 40+ real layouts.
          </p>
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg font-medium mb-2">
            Question: What if it doesn’t work for me?
          </p>
          <p className="text-gray-200 lg:text-2xl md:text-xl text-lg mb-6">
            Answer: If you're not happy with the Grid Attack game and think you haven't learned
            anything new about css grid, you can reach out by email hi@codingfantasy.com in the
            first 30 days and we'll refund your purchase.
          </p>
        </div>
      </div>
    </>
  )
}

export default Landing
